// `render_block` tool — lets the chat agent surface results via the
// stable kernel render primitives. The tool itself does no work: it
// pipes the (spec, data) pair through to `details` so ChatConsole can
// mount KernelBlock with the same spec a saved kernel would use later.
//
// Keep this contract narrow on purpose. Whatever the model can say
// here today is exactly what kernels can say tomorrow. The valid
// `kind` values and their schemas are declared in
// src/lib/kernelPrimitiveSpecs.js (single source of truth).

import { Type } from "@earendil-works/pi-ai";
import { markRendered } from "./renderTracker.js";
import { SPEC_DOC } from "./kernelPrimitiveSpecs.js";
import { coerceJsonArg } from "./coerceJsonArg.js";

// SPEC_DOC is assembled from per-primitive doc strings in
// kernelPrimitiveSpecs.js so the tool description, the renderer
// (components/kernelPrimitives), and the validators (kernelStore,
// kernelTestTool) can't drift apart. Add a new kind by extending that
// module — this tool picks up the change automatically.

// Factory: every chat instantiates its own render_block so a
// successful call can stamp the per-chat renderTracker.
// suggest_kernel reads that flag as its precondition (a kernel
// without a render spec can't be persisted; suggesting before any
// render_block has fired only produces broken save cards). Closure
// over `getNamespaceKey` keeps render_block and suggest_kernel
// pointed at the same chat namespace.
export function createRenderBlockTool({ getNamespaceKey } = {}) {
    return {
        name: "render_block",
        label: "Render block",
        description:
            "Surface a result in the chat using one of the dashboard's stable render primitives — single-value tiles (stat / kpis / donut / sparkline), categorical lists (bars / rows / table), composition (treemap / sunburst), distribution (boxplot / scatter / radar / parallel), time/series (chart), density (heatmap / calendar / timeline), and flow (funnel / sankey / chord). Prefer this over pasting a JSON dump when the data is naturally visual or tabular.\n\n" +
            "Use this for the final-presentation step of an analysis. The (spec, data) pair you pass here is exactly the shape saved kernels will use later — anything you can render here today becomes a one-click reusable dashboard widget down the line. Keep the spec JSON-serialisable; no functions, no JSX.\n\n" +
            "IMPORTANT — `data` must be the ACTUAL inline JSON values (numbers, arrays, objects), not a reference name. There is no step-ref resolution in this tool. Strings like \"result\" or \"output\" are NOT resolved; they'll render as an empty block. If you computed values in a previous step (analytics_query, execute_python), inline the actual numbers/rows here. If you need ref-based replay, that's what saved kernels (renderSource) do — render_block is for one-shot rendering with values baked in.\n\n" +
            SPEC_DOC,
        parameters: Type.Object({
            // Typed as an object (not Type.Unknown) so the model's
            // tool-call decoder is steered to emit an object literal
            // rather than a JSON-encoded string — the cross-model
            // double-encoding that made canvases fail on some models
            // (mirrors analytics_query's typed `spec`). additionalProperties
            // is open because the real shape is per-`kind` and lives in
            // kernelPrimitiveSpecs.js. coerceJsonArg in execute() stays the
            // safety net for models that stringify anyway.
            spec: Type.Object({
                kind: Type.String({
                    description:
                        "The render primitive to use (e.g. \"chart\", \"bars\", \"stat\", \"treemap\"). See the spec doc below for the full set and each kind's fields.",
                }),
            }, {
                additionalProperties: true,
                description:
                    "Render spec — a JSON object (NOT a JSON-encoded string). Must include `kind` plus the per-kind fields. See description for valid shapes.",
            }),
            data: Type.Unknown({
                description:
                    "Computed values for this render — INLINE JSON only (numbers, arrays, objects). Shape depends on the kind (array of numbers for sparkline, array of rows for bars/table/rows, scalar or object for stat). Do not pass a string like \"result\" expecting it to resolve to a previous step's output; this tool has no ref resolution.",
            }),
        }),
        async execute(_id, args) {
            // Some model families double-encode object/array params as a
            // JSON string. Decode before use so a stringified spec doesn't
            // render as "invalid spec" on one model but work on another.
            const spec = coerceJsonArg(args.spec);
            const data = coerceJsonArg(args.data);
            const summary = describeBlock(spec, data);
            try {
                const ns = getNamespaceKey?.() || "default";
                markRendered(ns);
            } catch { /* tracker stamp is non-essential */ }
            return {
                content: [{ type: "text", text: summary }],
                details: { kind: "kernelBlock", spec, data },
            };
        },
    };
}

// Back-compat shim for any caller that still imports the singleton
// (test harnesses, kernel runner internals, etc.). Falls back to the
// "default" namespace — fine for those callers since they never share
// a chat with the live agent.
export const renderBlockTool = createRenderBlockTool({ getNamespaceKey: () => "default" });

function describeBlock(spec, data) {
    if (!spec || typeof spec !== "object") return "rendered (invalid spec)";
    const kind = spec.kind || "unknown";
    if (Array.isArray(data)) {
        return `rendered ${kind} block · ${data.length} row${data.length === 1 ? "" : "s"}`;
    }
    return `rendered ${kind} block`;
}
